import { useForm, usePage } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { FormSection } from '@/components/backoffice/FormSection'
import { FieldEditor } from '@/components/backoffice/FieldEditor'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { EntitySummary, FieldDefinition, JurisdictionOptions, PageProps } from '@/types'

type Props = PageProps & {
  entity: EntitySummary
  fields: FieldDefinition[]
  fields_api_path: string
  ordering_api_path: string
  jurisdiction_options: JurisdictionOptions
}

export default function EntitiesEdit() {
  const { entity, fields, fields_api_path, ordering_api_path, jurisdiction_options } = usePage<Props>().props
  const { data, setData, patch, processing, errors } = useForm({
    name: entity.name,
    suffix: entity.suffix || '',
    description: entity.description || '',
    country_id: entity.country_id || '',
    state_id: entity.state_id || '',
    local_area_id: entity.local_area_id || '',
    filing_type: entity.filing_type,
    state_fee_cents: entity.state_fee_cents,
    service_fee_cents: entity.service_fee_cents,
    processing_days_min: entity.processing_days_min || 0,
    processing_days_max: entity.processing_days_max || 0,
    allows_anonymous_ownership: entity.allows_anonymous_ownership,
    requires_public_member_disclosure: entity.requires_public_member_disclosure,
  })

  const selectClass = "flex h-9 w-full items-center rounded-md border border-input bg-transparent pl-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"

  return (
    <BackofficeLayout title={`Edit ${entity.name}`} breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Entities', href: '/backoffice/entities' }, { label: entity.name, href: `/backoffice/entities/${entity.id}` }, { label: 'Edit' }]}>
      <div className="space-y-6">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Edit {entity.name}</h1>
        </div>

        <form onSubmit={e => { e.preventDefault(); patch(`/backoffice/entities/${entity.id}`) }} className="max-w-2xl space-y-6">
          <FormSection title="Identification">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Name</label>
                <Input className="h-8 text-sm" value={data.name} onChange={e => setData('name', e.target.value)} />
                {errors.name && <p className="text-[12px] text-destructive">{errors.name}</p>}
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Suffix</label>
                <Input className="h-8 text-sm" value={data.suffix} onChange={e => setData('suffix', e.target.value)} placeholder="LLC, Inc., Corp." />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Description</label>
              <Textarea className="text-sm min-h-15" value={data.description} onChange={e => setData('description', e.target.value)} />
            </div>
          </FormSection>

          <FormSection title="Jurisdiction">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Country</label>
                <select value={data.country_id} onChange={e => setData(d => ({ ...d, country_id: e.target.value, state_id: '', local_area_id: '' }))} className={selectClass}>
                  <option value="">Select...</option>
                  {jurisdiction_options.countries.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">State</label>
                <select value={data.state_id} onChange={e => setData(d => ({ ...d, state_id: e.target.value, local_area_id: '' }))} className={selectClass} disabled={!data.country_id}>
                  <option value="">Select...</option>
                  {jurisdiction_options.states.filter(s => s.country_id === data.country_id).map(s => <option key={s.id} value={s.id}>{s.name} ({s.code})</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Local Area</label>
                <select value={data.local_area_id} onChange={e => setData('local_area_id', e.target.value)} className={selectClass} disabled={!data.state_id}>
                  <option value="">None</option>
                  {jurisdiction_options.local_areas.filter(a => a.state_id === data.state_id).map(a => <option key={a.id} value={a.id}>{a.name} ({a.code})</option>)}
                </select>
              </div>
            </div>
          </FormSection>

          <FormSection title="Configuration">
            <div className="space-y-1.5">
              <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Filing Type</label>
              <Input className="h-8 text-sm" value={data.filing_type} onChange={e => setData('filing_type', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">State Fee (cents)</label>
                <Input className="h-8 text-sm" type="number" value={data.state_fee_cents} onChange={e => setData('state_fee_cents', parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Service Fee (cents)</label>
                <Input className="h-8 text-sm" type="number" value={data.service_fee_cents} onChange={e => setData('service_fee_cents', parseInt(e.target.value) || 0)} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Processing Days (min)</label>
                <Input className="h-8 text-sm" type="number" value={data.processing_days_min} onChange={e => setData('processing_days_min', parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[12px] font-medium text-muted-foreground uppercase tracking-wider">Processing Days (max)</label>
                <Input className="h-8 text-sm" type="number" value={data.processing_days_max} onChange={e => setData('processing_days_max', parseInt(e.target.value) || 0)} />
              </div>
            </div>
          </FormSection>

          <FormSection title="Options">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox id="anon" checked={data.allows_anonymous_ownership} onCheckedChange={c => setData('allows_anonymous_ownership', !!c)} />
                <label htmlFor="anon" className="text-sm">Allows anonymous ownership</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="disclosure" checked={data.requires_public_member_disclosure} onCheckedChange={c => setData('requires_public_member_disclosure', !!c)} />
                <label htmlFor="disclosure" className="text-sm">Requires public member disclosure</label>
              </div>
            </div>
          </FormSection>

          <div className="flex gap-2 pt-2">
            <Button type="submit" size="sm" disabled={processing} className="h-8 text-[13px]">Save Changes</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => window.history.back()} className="h-8 text-[13px]">Cancel</Button>
          </div>
        </form>

        <div className="max-w-2xl">
          <FieldEditor fieldsApiPath={fields_api_path} orderingApiPath={ordering_api_path} fields={fields} />
        </div>
      </div>
    </BackofficeLayout>
  )
}
